// RM Opening Stock — Feb 27, 2026.
//
// Dedicated sidebar page that consolidates the per-RM-code opening
// balances that used to live behind RM Log's "Add RM Stock" modal.
//   • Rows auto-seeded from every rm_code found in Part Master
//   • Inline edit → PUT /rm/pool-opening/{code}
//   • Template / Import / Export via /api/rm-opening-stock/*
//   • Smart search bar

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import Layout from "../components/Layout";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  Package, MagnifyingGlass, PencilSimple, DownloadSimple,
  UploadSimple, FileText, ArrowsClockwise, Lock,
} from "@phosphor-icons/react";

const f2 = (v) => Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function RMOpeningStock() {
  // Year-based gate (Swami@YYYY). Same convention as Passkeys /
  // RUN MTR — cached in sessionStorage so the operator doesn't retype
  // on every sidebar visit.
  const expectedPwd = `Swami@${new Date().getFullYear()}`;
  const STORAGE_KEY = "rm_opening_stock_unlocked";
  const [unlocked, setUnlocked] = useState(() => {
    try { return sessionStorage.getItem(STORAGE_KEY) === expectedPwd; }
    catch { return false; }
  });
  const [gatePwd, setGatePwd] = useState("");
  const [gateErr, setGateErr] = useState("");
  const unlock = (e) => {
    e.preventDefault();
    if (gatePwd.trim() === expectedPwd) {
      try { sessionStorage.setItem(STORAGE_KEY, expectedPwd); } catch {/*ignore*/}
      setUnlocked(true); setGateErr("");
    } else {
      setGateErr("Wrong password. Contact your administrator if you don't have it.");
    }
  };

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // { rm_code, grade, material_type, opening_kg }
  const [openingValue, setOpeningValue] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/rm-opening-stock`, { skipPeriod: true });
      setRows(r.data || []);
    } catch { toast.error("Failed to load RM opening stock"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      (r.rm_code || "").toLowerCase().includes(q) ||
      (r.grade || "").toLowerCase().includes(q) ||
      (r.material_type || "").toLowerCase().includes(q)
    );
  }, [rows, search]);

  const totals = useMemo(() => filtered.reduce((s, r) => s + Number(r.opening_kg || 0), 0), [filtered]);

  const openEdit = (r) => {
    setEditing(r);
    setOpeningValue(String(r.opening_kg || 0));
  };
  const saveEdit = async () => {
    if (!editing) return;
    const qty = Number(openingValue);
    if (Number.isNaN(qty) || qty < 0) { toast.error("Enter a non-negative number"); return; }
    try {
      await axios.put(`${API}/rm/pool-opening/${encodeURIComponent(editing.rm_code)}`, { opening_kg: qty });
      toast.success(`${editing.rm_code} opening saved`);
      setEditing(null); setOpeningValue("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const downloadTemplate = async () => {
    try {
      const r = await axios.get(`${API}/rm-opening-stock/template`, {
        responseType: "blob", skipPeriod: true,
      });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "rm-opening-stock-template.xlsx";
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Template download failed"); }
  };

  const exportExcel = async () => {
    try {
      const r = await axios.get(`${API}/rm-opening-stock/export`, {
        responseType: "blob", skipPeriod: true,
      });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `rm-opening-stock_${new Date().toISOString().slice(0,10)}.xlsx`;
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const importExcel = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await axios.post(`${API}/rm-opening-stock/import`, fd, {
        headers: { "Content-Type": "multipart/form-data" }, skipPeriod: true,
      });
      const errCount = (r.data.errors || []).length;
      toast.success(`Imported ${r.data.saved || 0} rows${errCount ? ` · ${errCount} error(s)` : ""}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <Layout title="RM Opening Stock">
      {!unlocked ? (
        <div className="p-6 max-w-2xl mx-auto">
          <Card className="p-8 border-2 border-dashed border-[#CBD5E1] bg-[#F8FAFC]" data-testid="rmopen-gate">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="w-16 h-16 rounded-full bg-[#1D2951] flex items-center justify-center shrink-0">
                <Lock size={28} weight="bold" className="text-white" />
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-lg font-black text-[#1D2951]">RM Opening Stock — Restricted</h3>
                <p className="text-xs text-[#64748B] mt-1">
                  Opening balances drive every downstream calculation.
                  Enter the annual master password to view or edit them.
                </p>
              </div>
              <form onSubmit={unlock} className="flex items-end gap-2 shrink-0 w-full md:w-auto">
                <div className="flex-1 md:w-56">
                  <Label className="text-[10px] font-bold uppercase tracking-widest text-[#64748B]">Access Password</Label>
                  <Input type="password" value={gatePwd}
                    onChange={e => { setGatePwd(e.target.value); setGateErr(""); }}
                    placeholder="Enter access password"
                    className="h-10 mt-1" data-testid="rmopen-gate-pwd" autoFocus />
                </div>
                <Button type="submit" className="bg-[#1D2951] hover:bg-[#2A3B73] h-10" data-testid="rmopen-gate-unlock">
                  Unlock
                </Button>
              </form>
            </div>
            {gateErr && <p className="text-xs text-[#EF4444] mt-3 text-center" data-testid="rmopen-gate-err">{gateErr}</p>}
          </Card>
        </div>
      ) : (
      <div className="p-4 md:p-6 space-y-4 max-w-[1400px] mx-auto">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-[#1D2951]/5 text-[#1D2951]">
              <Package size={20} weight="fill" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-black tracking-tight text-[#1D2951]" style={{ fontFamily: "Chivo" }}>
                RM Opening Stock
              </h1>
              <p className="text-xs md:text-sm text-[#64748B] mt-0.5">
                Set the opening balance (Kg) for every RM code. These values feed RM Planning and RM Log.
              </p>
            </div>
          </div>
          <Button variant="outline" onClick={load} className="h-9" data-testid="rmopen-refresh">
            <ArrowsClockwise size={14} className="mr-1" /> Refresh
          </Button>
        </div>

        {/* Toolbar */}
        <Card className="p-3 border border-[#E2E8F0]">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[220px]">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
              <Input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search RM code, grade or material type…"
                className="h-9 pl-9" data-testid="rmopen-search" />
            </div>
            <Button variant="outline" onClick={downloadTemplate} className="h-9" data-testid="rmopen-template">
              <FileText size={14} className="mr-1" /> Template
            </Button>
            <Button variant="outline" onClick={exportExcel} className="h-9" data-testid="rmopen-export">
              <DownloadSimple size={14} className="mr-1" /> Export
            </Button>
            <input type="file" ref={fileRef} accept=".xlsx,.xls"
              onChange={e => importExcel(e.target.files?.[0])} className="hidden" />
            <Button variant="outline" onClick={() => fileRef.current?.click()} className="h-9" data-testid="rmopen-import">
              <UploadSimple size={14} className="mr-1" /> Import
            </Button>
            <span className="text-[11px] text-[#64748B] font-semibold pr-2">
              {filtered.length} / {rows.length} codes
            </span>
          </div>
        </Card>

        {/* Total */}
        <Card className="p-4 border border-[#E2E8F0] flex items-baseline justify-between">
          <div className="text-[10px] font-black uppercase tracking-widest text-[#1D2951]">Total Opening (Kg)</div>
          <div className="text-2xl md:text-3xl font-mono font-black text-[#0F172A]">{f2(totals)}</div>
        </Card>

        {/* Table */}
        <Card className="border border-[#E2E8F0] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#1D2951] text-white">
                <tr>
                  <th className="px-3 py-3 text-left font-semibold">RM Code</th>
                  <th className="px-3 py-3 text-left font-semibold">Grade</th>
                  <th className="px-3 py-3 text-left font-semibold">Material Type</th>
                  <th className="px-3 py-3 text-right font-semibold">Opening (Kg)</th>
                  <th className="px-3 py-3 text-left font-semibold">Updated</th>
                  <th className="px-3 py-3 text-center font-semibold w-16">Edit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F1F5F9]">
                {loading && <tr><td colSpan={6} className="py-12 text-center text-[#64748B]">Loading…</td></tr>}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={6} className="py-16 text-center text-[#94A3B8]">
                    {rows.length === 0
                      ? <>No RM codes yet — assign an <b>rm_code</b> to a part in Part Master first.</>
                      : "No RM code matches your search."}
                  </td></tr>
                )}
                {filtered.map((r) => (
                  <tr key={r.rm_code} className={`hover:bg-[#F8FAFC] ${r.orphan ? "bg-[#FEF3C7]/30" : ""}`}
                      data-testid={`rmopen-row-${r.rm_code}`}>
                    <td className="px-3 py-2 font-mono font-bold text-[#1D2951]">
                      {r.rm_code}
                      {r.orphan && <span className="ml-2 text-[10px] font-bold text-[#B45309]" title="No matching part in Part Master">(orphan)</span>}
                    </td>
                    <td className="px-3 py-2 text-[#334155]">{r.grade || "—"}</td>
                    <td className="px-3 py-2 capitalize text-[#334155]">{r.material_type || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono font-bold text-[#0F172A]">{f2(r.opening_kg)}</td>
                    <td className="px-3 py-2 text-[11px] text-[#64748B]">
                      {r.updated_at
                        ? `${new Date(r.updated_at).toLocaleDateString()} · ${r.updated_by || ""}`
                        : "—"}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <button onClick={() => openEdit(r)}
                        className="w-8 h-8 flex items-center justify-center rounded-md border border-[#E2E8F0] text-[#334155] hover:border-[#1D2951] hover:text-[#1D2951]"
                        title="Edit opening"
                        data-testid={`rmopen-edit-${r.rm_code}`}>
                        <PencilSimple size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
      )}

      {/* Opening edit modal */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="max-w-md" data-testid="rmopen-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-black text-[#1D2951]">
              Set Opening — {editing?.rm_code}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Field label="RM Code / Grade">
              <Input value={`${editing?.rm_code || ""} · ${editing?.grade || "—"}`}
                readOnly className="h-9 bg-[#F1F5F9]" />
            </Field>
            <Field label="Opening (Kg) *">
              <Input type="number" step="0.001" value={openingValue}
                onChange={e => setOpeningValue(e.target.value)}
                className="h-9 font-mono" autoFocus
                data-testid="rmopen-value" />
            </Field>
            <p className="text-[11px] text-[#94A3B8]">
              Replaces the existing opening (not additive). Enter 0 to clear.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEdit} className="bg-[#1D2951] hover:bg-[#2A3B73]" data-testid="rmopen-save">
              Save Opening
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] font-bold uppercase tracking-widest text-[#64748B]">{label}</Label>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}
